import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../utils/supabaseClient';

const PostPitchDeckForm = ({ pitchDeck, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [formData, setFormData] = useState({
    title: pitchDeck?.name || '',
    description: '',
    objective: 'Funding', // Default value
    previewImageUrl: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB limit');
      return;
    }

    try {
      setUploadStatus('uploading');
      
      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `gallery_previews/${fileName}`;
      
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('pitch_deck_assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (uploadError) throw uploadError;
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('pitch_deck_assets')
        .getPublicUrl(filePath);
      
      setFormData({
        ...formData,
        previewImageUrl: publicUrl
      });
      
      setUploadStatus('success');
      setError(null);
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadStatus('error');
      setError('Failed to upload preview image. You can still post without a preview image.');
    }
  };

  // Function to extract images from content
  const extractImageFromContent = (content) => {
    if (!content) return null;
    
    // Try to extract image URL from HTML content
    const imgMatch = content.match(/<img[^>]+src="([^">]+)"/i);
    if (imgMatch && imgMatch[1]) {
      return imgMatch[1];
    }
    
    // If that fails, try to find any URL that looks like an image
    const urlMatch = content.match(/(https?:\/\/[^"\s]+\.(jpe?g|png|gif|webp))/i);
    if (urlMatch && urlMatch[1]) {
      return urlMatch[1];
    }
    
    return null;
  };

  // Function to find a suitable preview image from the pitch deck
  const findPreviewImageFromDeck = () => {
    // Check for slides in the content object
    if (pitchDeck.content && pitchDeck.content.slides) {
      const slides = pitchDeck.content.slides;
      
      // Try to get slide order
      const slideOrder = slides.slide_order || Object.keys(slides).filter(key => 
        key !== 'slide_order' && 
        key !== 'updated_at' && 
        key !== 'background_fit' &&
        key !== 'background_image'
      );
      
      // Check each slide for images
      for (const slideKey of slideOrder) {
        const slide = slides[slideKey];
        
        // Check if slide has images array
        if (slide.images && slide.images.length > 0) {
          const firstImage = slide.images[0];
          return typeof firstImage === 'string' ? firstImage : firstImage.url;
        }
        
        // Try to extract image from content
        if (slide.content) {
          const extractedImage = extractImageFromContent(slide.content);
          if (extractedImage) return extractedImage;
        }
      }
    }
    
    // Try the legacy slides format
    if (pitchDeck.slides) {
      const slides = pitchDeck.slides;
      
      // Try to get slide order
      const slideOrder = slides.slide_order || Object.keys(slides).filter(key => 
        key !== 'slide_order' && 
        key !== 'updated_at' && 
        key !== 'background_fit' &&
        key !== 'background_image'
      );
      
      // Check each slide for images
      for (const slideKey of slideOrder) {
        const slide = slides[slideKey];
        
        // Check if slide has images array
        if (slide.images && slide.images.length > 0) {
          const firstImage = slide.images[0];
          return typeof firstImage === 'string' ? firstImage : firstImage.url;
        }
        
        // Try to extract image from content
        if (slide.content) {
          const extractedImage = extractImageFromContent(slide.content);
          if (extractedImage) return extractedImage;
        }
      }
    }
    
    // No suitable image found
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Get the first slide from the pitch deck as a preview if no custom image is provided
      let previewImage = formData.previewImageUrl;
      
      if (!previewImage) {
        previewImage = findPreviewImageFromDeck();
      }

      // Insert the gallery pitch
      const { data, error } = await supabase
        .from('gallery_pitches')
        .insert({
          pitch_deck_id: pitchDeck.id,
          product_id: pitchDeck.product_id,
          user_id: pitchDeck.user_id,
          title: formData.title,
          description: formData.description,
          objective: formData.objective,
          preview_image_url: previewImage,
          is_published: true
        })
        .select()
        .single();

      if (error) throw error;

      // Try to add a function to register view count increment, but don't fail if it doesn't exist
      try {
        await supabase.rpc('create_gallery_view_count_function', {});
      } catch (rpcError) {
        console.warn('View count function creation failed:', rpcError);
        // Continue anyway - this is not critical
      }

      if (onSuccess) {
        onSuccess(data);
      }

      // Navigate to the gallery pitch view page
      navigate(`/gallery/pitch/${data.id}`);
    } catch (error) {
      console.error('Error publishing pitch deck:', error);
      setError('Failed to publish pitch deck. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Post Pitch Deck to Gallery</h2>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="mb-4">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            Provide a brief overview of your pitch deck and what you're looking for.
          </p>
        </div>

        <div className="mb-4">
          <label htmlFor="objective" className="block text-sm font-medium text-gray-700 mb-1">
            Objective *
          </label>
          <select
            id="objective"
            name="objective"
            value={formData.objective}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="Funding">Funding</option>
            <option value="Partnering">Partnering</option>
            <option value="Request Services">Request Services</option>
            <option value="License Out">License Out</option>
          </select>
        </div>

        <div className="mb-6">
          <label htmlFor="previewImage" className="block text-sm font-medium text-gray-700 mb-1">
            Preview Image (Optional)
          </label>
          <input
            type="file"
            id="previewImage"
            accept="image/*"
            onChange={handleImageChange}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={uploadStatus === 'uploading'}
          />
          <p className="text-sm text-gray-500 mt-1">
            Upload a preview image for your pitch deck. If none is provided, we'll try to use an image from your slides.
          </p>
          
          {uploadStatus === 'uploading' && (
            <div className="mt-2 text-blue-600 flex items-center">
              <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </div>
          )}
          
          {uploadStatus === 'error' && (
            <div className="mt-2 text-yellow-600">
              Image upload failed. You can still post without a preview image.
            </div>
          )}
          
          {formData.previewImageUrl && (
            <div className="mt-2">
              <img 
                src={formData.previewImageUrl} 
                alt="Preview" 
                className="h-32 object-contain border rounded"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className={`px-4 py-2 rounded-md text-white ${
              isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
            }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Posting...' : 'Post to Gallery'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PostPitchDeckForm; 